import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCreateOwnerAccountLogin } from "@workspace/api-client-react";
const OWNER_SESSION_QUERY_KEY = ["/api/owner/session"] as const;

export type OwnerSessionStatus = {
  authenticated: boolean;
  username?: string;
  role?: "admin" | "staff";
  modules?: string[];
  passwordConfigured?: boolean;
  ownerKeyFallback?: boolean;
};

export async function ownerRequestJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, { ...init, credentials: "same-origin" });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: unknown } | null;
    throw new Error(
      typeof payload?.error === "string" ? payload.error : "Owner session request failed.",
    );
  }
  return response.json() as Promise<T>;
}

async function getOwnerSession() {
  return ownerRequestJson<OwnerSessionStatus>("/api/owner/session");
}

function isOwnerDataQuery(queryKey: readonly unknown[]) {
  return queryKey.some(
    (part) =>
      typeof part === "string" &&
      part.includes("/api/owner/") &&
      part !== OWNER_SESSION_QUERY_KEY[0],
  );
}

export function useOwnerSession() {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: OWNER_SESSION_QUERY_KEY,
    queryFn: getOwnerSession,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const passwordLoginMutation = useCreateOwnerAccountLogin({
    mutation: {
      onSuccess: (session) => {
        queryClient.setQueryData(OWNER_SESSION_QUERY_KEY, session);
      },
    },
  });
  const logoutMutation = useMutation({
    mutationFn: () => ownerRequestJson<OwnerSessionStatus>("/api/owner/session", { method: "DELETE" }),
    onSuccess: (session) => {
      queryClient.setQueryData(OWNER_SESSION_QUERY_KEY, session);
      queryClient.removeQueries({ predicate: (query) => isOwnerDataQuery(query.queryKey) });
    },
  });

  return {
    isAuthenticated: sessionQuery.data?.authenticated === true,
    isLoading: sessionQuery.isLoading,
    isError: sessionQuery.isError,
    error: sessionQuery.error,
    loginWithPassword: (credentials: { username: string; password: string }) =>
      passwordLoginMutation.mutateAsync({ data: credentials }),
    isPasswordLoginPending: passwordLoginMutation.isPending,
    passwordLoginError: passwordLoginMutation.error,
    canAccess: (module: string) =>
      sessionQuery.data?.role === "admin" ||
      sessionQuery.data?.modules?.includes(module) === true,
    refreshSession: () => sessionQuery.refetch(),
    logout: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
  };
}
